import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  hashStringToTagColor,
  MarkdownEditor,
  TagPicker,
  Text,
  TextField,
  useToast,
  type TagColor,
  type TagOption,
} from '@chatsdet/ui';
import {
  useCreatePrompt,
  useUpdatePrompt,
  type PromptDetail,
  type PromptTag,
} from '../api';
import {
  useCreateTagMutation,
  useSetEntityTagsMutation,
  useTagsQuery,
} from '../../../hooks/useTags';

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present ⇒ edit mode; absent ⇒ create mode. */
  editing?: PromptDetail | null;
  /** Whether the viewer may author global Cofactor prompts. */
  canCreateGlobal: boolean;
};

/**
 * The org tags a prompt is opened with, derived synchronously from the prompt
 * detail payload (which already embeds the assigned org tags). Seeding from this
 * — rather than waiting on an async entity-tags query — is what prevents an
 * early Save from sending an empty `tagIds` set and clearing the prompt's tags.
 *
 * Returns `[]` for create mode and for global prompts (which use free-string
 * `globalTags`, not the org tag rows; their tags carry a null id).
 */
export function initialOrgTags(editing: PromptDetail | null | undefined): TagOption[] {
  if (!editing || editing.scope !== 'org') return [];
  return editing.tags
    .filter((t): t is PromptTag & { id: string } => t.id !== null)
    .map((t) => ({ id: t.id, name: t.name, color: t.color }));
}

/**
 * Create / edit dialog for a prompt. Reuses the shared `MarkdownEditor` (the
 * same Tiptap editor the docs app uses). Superadmins additionally get a "global
 * Cofactor prompt" toggle on create.
 *
 * The form lives in a child (`PromptEditorForm`) that is **keyed** by the edit
 * target + open state, so its `useState` re-initializes from `editing` on every
 * open. That makes the `MarkdownEditor` mount *with* the prompt body (rather
 * than seeding via an effect, which the editor's mount-time `onChange('')` would
 * clobber).
 */
export function PromptEditorDialog({ isOpen, onOpenChange, editing, canCreateGlobal }: Props) {
  const mode = editing ? 'edit' : 'create';
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="4xl">
      <span />
      <Dialog
        eyebrow={mode === 'edit' ? 'Edit prompt' : 'New prompt'}
        title={mode === 'edit' ? editing?.title || 'Edit prompt' : 'Create a prompt'}
      >
        {isOpen && (
          <PromptEditorForm
            key={`${editing?.id ?? 'new'}:${isOpen}`}
            editing={editing ?? null}
            canCreateGlobal={canCreateGlobal}
            onClose={() => onOpenChange(false)}
          />
        )}
      </Dialog>
    </DialogTrigger>
  );
}

/**
 * The form body + footer for one open session. State is initialized from
 * `editing` at mount (the parent keys this component so it remounts per target).
 *
 * Tags are scoped: ORG prompts link to the org's real tags via `TagPicker`;
 * GLOBAL prompts carry arbitrary free-string tags saved on the prompt itself.
 */
function PromptEditorForm({
  editing,
  canCreateGlobal,
  onClose,
}: {
  editing: PromptDetail | null;
  canCreateGlobal: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createPrompt = useCreatePrompt();
  const updatePrompt = useUpdatePrompt();

  const mode = editing ? 'edit' : 'create';
  // The prompt being authored is global when editing a global row, or when the
  // create-mode toggle is on.
  const promptIsGlobal = editing ? editing.scope === 'global' : false;

  const [title, setTitle] = React.useState(editing?.title ?? '');
  const [description, setDescription] = React.useState(editing?.description ?? '');
  const [content, setContent] = React.useState(editing?.content ?? '');
  const [isGlobal, setIsGlobal] = React.useState(false);
  // Org prompts: real org tags, seeded synchronously from the detail payload so
  // an early Save can never replace them with an empty set. Global prompts use
  // arbitrary free-string names instead.
  const [orgTags, setOrgTags] = React.useState<TagOption[]>(() => initialOrgTags(editing));
  const [globalTags, setGlobalTags] = React.useState<string[]>(
    editing && editing.scope === 'global' ? editing.tags.map((t) => t.name) : []
  );

  // Whether to author a global prompt: editing a global row, or the create toggle.
  const willBeGlobal = editing ? promptIsGlobal : isGlobal;

  // Org-tag wiring (mirrors WorkflowSettingsOverlay / IssueDrawerV2).
  const tagsQuery = useTagsQuery();
  const createTagMutation = useCreateTagMutation();
  const setEntityTagsMutation = useSetEntityTagsMutation();

  const allOrgTags: TagOption[] = React.useMemo(() => {
    const data = tagsQuery.data;
    if (!data?.ok || !data.data) return [];
    return data.data.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }));
  }, [tagsQuery.data]);

  const handleCreateTag = React.useCallback(
    async (name: string, color: TagColor): Promise<TagOption> => {
      const result = await createTagMutation.mutateAsync({ name, color });
      return { id: result.tag.id, name: result.tag.name, color: result.tag.color };
    },
    [createTagMutation]
  );

  const isPending =
    createPrompt.isPending || updatePrompt.isPending || setEntityTagsMutation.isPending;
  const canSave = title.trim().length > 0 && content.trim().length > 0 && !isPending;

  const handleSave = async () => {
    const input = {
      title: title.trim(),
      content,
      description: description.trim() || null,
    };
    try {
      if (editing) {
        await updatePrompt.mutateAsync({
          id: editing.id,
          input: willBeGlobal ? { ...input, globalTags } : input,
        });
        if (editing.scope === 'org') {
          await setEntityTagsMutation.mutateAsync({
            entityType: 'prompt',
            entityId: editing.id,
            tagIds: orgTags.map((t) => t.id),
          });
        }
        toast.success('Prompt updated');
      } else if (isGlobal) {
        await createPrompt.mutateAsync({ ...input, scope: 'global', globalTags });
        toast.success('Global prompt created');
      } else {
        const created = await createPrompt.mutateAsync({ ...input, scope: 'org' });
        if (orgTags.length > 0) {
          await setEntityTagsMutation.mutateAsync({
            entityType: 'prompt',
            entityId: created.data.id,
            tagIds: orgTags.map((t) => t.id),
          });
        }
        toast.success('Prompt created');
      }
      // Org tags are attached via a separate mutation that runs *after* the
      // create/update query invalidation, so refetch the prompt list/detail
      // once more here to surface the new tags in the rail and viewer.
      await queryClient.invalidateQueries({ queryKey: ['prompts'] });
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save prompt');
    }
  };

  return (
    <>
      <DialogContent>
        <Flex direction="column" gap="md">
          <TextField
            label="Title"
            value={title}
            onChange={setTitle}
            placeholder="e.g. Install the Cofactor CLI"
          />
          <TextField
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="One-line summary shown on the card (optional)"
          />
          <div>
            <Text size="sm" weight="medium" className="mb-1 block">
              Prompt
            </Text>
            <MarkdownEditor
              value={content}
              onChange={setContent}
              placeholder="Write the prompt in markdown…"
              /* dialog-frame-ok: markdown editor surface boundary, not a field group */
              className="min-h-[320px] rounded-md border border-border"
            />
          </div>

          {willBeGlobal ? (
            <GlobalTagsInput value={globalTags} onChange={setGlobalTags} />
          ) : (
            <TagPicker
              label="Tags"
              placeholder="Add tags…"
              tags={allOrgTags}
              selectedTags={orgTags}
              onChange={setOrgTags}
              onCreateTag={handleCreateTag}
              isLoading={tagsQuery.isLoading}
            />
          )}

          {canCreateGlobal && mode === 'create' && (
            <Checkbox
              isSelected={isGlobal}
              onChange={setIsGlobal}
              label="Global Cofactor prompt"
              description="Visible to every org and read-only to them. Superadmin only."
            />
          )}
        </Flex>
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onPress={onClose} isDisabled={isPending}>
          Cancel
        </Button>
        <Button variant="primary" onPress={handleSave} isDisabled={!canSave}>
          {isPending ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create prompt'}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * Free-string tag input for GLOBAL prompts. Reuses `TagPicker` but treats each
 * tag name as its own option (id == name), so superadmins can attach arbitrary
 * labels that aren't backed by an org's tag rows. Colors are derived from the
 * name for a stable look.
 */
function GlobalTagsInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const selected: TagOption[] = React.useMemo(
    () => value.map((name) => ({ id: name, name, color: hashStringToTagColor(name) })),
    [value]
  );
  return (
    <TagPicker
      label="Tags"
      description="Arbitrary labels shown on this Cofactor prompt for every org."
      placeholder="Add tags…"
      tags={selected}
      selectedTags={selected}
      onChange={(tags) => onChange(tags.map((t) => t.name))}
      onCreateTag={async (name) => ({ id: name, name, color: hashStringToTagColor(name) })}
    />
  );
}
